import type {ProfileRoleDetails, UserModel} from '../../auth/core/_models'
import type {LibraryMutationContext, LibraryTemplateFilters} from './libraryApi'
import {
  canSelectOperationsLibraryModeForUser,
  coerceOperationsLibraryMode,
  resolveAuthenticatedCompanyGroupAdminId,
  resolveOperationsLibraryRoleName,
} from './libraryScopeRules'

export type LibrarySelections = {
  selectedCompanyGroupAdminId?: number | null
  selectedCompanyAdminId?: number | null
  selectedVesselId?: number | null
  selectedVesselType?: string | null
}

const asNumber = (value: unknown) => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : undefined
}

const asVesselType = (value: unknown) => {
  const text = String(value ?? '').trim()
  return text ? text.toUpperCase() : undefined
}

export const buildLibraryMutationContext = (
  currentUser: UserModel | undefined,
  roleDetails: ProfileRoleDetails | null | undefined,
  requestedMode: 'master' | 'company',
  selections: LibrarySelections
): LibraryMutationContext => {
  const normalizedRoleName = resolveOperationsLibraryRoleName(currentUser?.role?.roleName, roleDetails)
  const canSelectLibraryMode = canSelectOperationsLibraryModeForUser(normalizedRoleName, currentUser)
  const mode = coerceOperationsLibraryMode(requestedMode, canSelectLibraryMode)
  if (mode === 'master') {
    return {mode: 'master'}
  }

  const authenticatedCompanyGroupAdminId = asNumber(resolveAuthenticatedCompanyGroupAdminId(currentUser))
  const authenticatedCompanyAdminId =
    asNumber(currentUser?.companyAdminId) ?? asNumber(currentUser?.roleEntityId)
  const authenticatedVesselId = asNumber(currentUser?.vessel?.id)
  const authenticatedVesselType = asVesselType(currentUser?.vessel?.vesselType)

  if (normalizedRoleName === 'CREW') {
    return {
      mode: 'company',
      companyGroupAdminId:
        authenticatedCompanyGroupAdminId ??
        asNumber(currentUser?.vessel?.companyGroupAdminId) ??
        asNumber((currentUser?.vessel as any)?.companyGroupAdmin?.id),
      companyAdminId:
        authenticatedCompanyAdminId ??
        asNumber(currentUser?.vessel?.companyAdminId) ??
        asNumber(currentUser?.vessel?.companyAdmin?.id),
      vesselId: authenticatedVesselId,
      vesselType: authenticatedVesselType,
    }
  }

  if (normalizedRoleName === 'COMPANY_ADMIN') {
    return {
      mode: 'company',
      companyGroupAdminId: authenticatedCompanyGroupAdminId,
      companyAdminId: authenticatedCompanyAdminId,
      vesselId: asNumber(selections.selectedVesselId),
      vesselType: asVesselType(selections.selectedVesselType),
    }
  }

  if (
    normalizedRoleName === 'COMPANY_GROUP_ADMIN' ||
    ((normalizedRoleName === 'OPERATOR' || normalizedRoleName === 'GLOBAL_OPERATOR') && authenticatedCompanyGroupAdminId)
  ) {
    return {
      mode: 'company',
      companyGroupAdminId: authenticatedCompanyGroupAdminId,
      companyAdminId: asNumber(selections.selectedCompanyAdminId),
      vesselId: asNumber(selections.selectedVesselId),
      vesselType: asVesselType(selections.selectedVesselType),
    }
  }

  return {
    mode: 'company',
    companyGroupAdminId: asNumber(selections.selectedCompanyGroupAdminId),
    companyAdminId: asNumber(selections.selectedCompanyAdminId),
    vesselId: asNumber(selections.selectedVesselId),
    vesselType: asVesselType(selections.selectedVesselType),
  }
}

export const buildLibraryTemplateFilters = (
  context: LibraryMutationContext,
  formType?: string
): LibraryTemplateFilters => ({
  companyGroupAdminId: context.companyGroupAdminId,
  companyAdminId: context.companyAdminId,
  formType,
})

export const hasLibraryMutationTarget = (context?: LibraryMutationContext) =>
  Boolean(
    context &&
      context.mode === 'company' &&
      (context.companyGroupAdminId || context.companyAdminId || context.vesselId || context.vesselType)
  )
